/**
 * 좌측 제어 패널 (계획서 §13.1, UI_design.pptx).
 *
 * 고장 발생, 조기 수리 완료, 현재시각 변경, 배속·재생, reset 을 담는다 (§14 단계 7-9).
 * `WAIT_HUMAN_START`·`WAIT_PLC_ACK` 에서는 시계 조작이 잠긴다 (§7.2).
 * 고장·계획 승인 대기 중에는 잠기지는 않지만 가상시계가 흐르지 않으므로,
 * 재생·배속·시각변경을 비활성화하고 다음에 할 일을 문장으로 알린다.
 */

import { useEffect, useState } from "react";

import { SPEED_OPTIONS } from "@/types/api";
import type { RunSnapshot, RunState } from "@/types/api";
import { formatTime, fromLocalInputValue, runStateLabel, toLocalInputValue } from "@/lib/format";

/** 시계가 멈춰 있는 상태별로 "지금 사람이 할 일"을 알려 준다 (§7.2). */
const FROZEN_NOTICES: Partial<Record<RunState, string>> = {
  IDLE: "▶ 재생을 누르면 운전을 시작합니다",
  INCIDENT_ACTIVE: "고장을 분석해 A/B 계획을 세우는 중입니다",
  PLANNING: "A/B 계획을 세우는 중입니다",
  WAIT_PLAN_APPROVAL:
    "오른쪽에서 A안·B안 중 하나를 승인하세요. 승인하면 기동시각까지 자동으로 진행합니다",
  WAIT_RISK_CONFIRMATION: "조기 수리 완료의 위험 확인에 답하세요",
  PLAN_GENERATION_FAILED: "계획 생성에 실패했습니다. 다시 시도하세요",
  COMPLETE: "시나리오가 끝났습니다. 처음부터 다시 (reset) 를 누르세요",
};

interface Props {
  snapshot: RunSnapshot;
  busy: boolean;
  error: string | null;
  onRaiseIncident: () => void;
  onEarlyComplete: () => void;
  onConfirmRisk: (confirmed: boolean) => void;
  onSeek: (isoValue: string) => void;
  onSpeed: (multiplier: number) => void;
  onPlayPause: () => void;
  onReset: () => void;
}

export function ControlPanel({
  snapshot,
  busy,
  error,
  onRaiseIncident,
  onEarlyComplete,
  onConfirmRisk,
  onSeek,
  onSpeed,
  onPlayPause,
  onReset,
}: Props): React.JSX.Element {
  const { clock, incident } = snapshot;
  const [seekValue, setSeekValue] = useState(() => toLocalInputValue(clock.virtual_at));

  // 사용자가 값을 고쳤는지로 판단한다. focus 로 판단하면 안 된다.
  // 버튼을 누를 때 blur 가 click 보다 먼저 일어나므로, focus 기준이면 입력값이
  // 현재 시각으로 되돌아간 뒤 seek 이 실행되어 "시각 변경이 안 먹는" 것처럼 보인다.
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) setSeekValue(toLocalInputValue(clock.virtual_at));
  }, [clock.virtual_at, dirty]);

  const locked = clock.is_locked;
  const awaitingRisk = clock.state === "WAIT_RISK_CONFIRMATION";
  // 잠기지 않았어도 흐르지 않는 상태가 있다. 재생 버튼이 조용히 먹통이 되지 않게 막는다.
  const clockFrozen = !clock.can_advance;
  const clockDisabled = locked || clockFrozen || busy;
  const frozenNotice = clockFrozen ? FROZEN_NOTICES[clock.state] : undefined;

  return (
    <aside className="control-panel" aria-label="운전 제어">
      <section>
        <h2>현재 시각</h2>
        <p className="clock-value">{formatTime(clock.virtual_at)}</p>
        <p className="clock-state">
          {runStateLabel(clock.state)} · {clock.speed_multiplier}배속{" "}
          {clock.is_playing ? "▶ 재생" : "⏸ 정지"}
        </p>
        {locked && (
          <p className="notice" role="status">
            인간 조작 대기 중이라 시각 변경이 잠겨 있습니다 (§7.2)
          </p>
        )}
        {!locked && frozenNotice && (
          <p className="notice" role="status">
            ⏸ 가상시계 정지 — {frozenNotice}
          </p>
        )}
      </section>

      <section>
        <h2>고장</h2>
        <button type="button" onClick={onRaiseIncident} disabled={busy || incident !== null}>
          고장 발생 (수리 30분)
        </button>
        <button
          type="button"
          onClick={onEarlyComplete}
          disabled={busy || incident === null || awaitingRisk}
        >
          수리 완료
        </button>
        {awaitingRisk && (
          <div className="risk-confirm" role="alertdialog" aria-label="위험 확인">
            <p>수리를 조기 완료하면 계획을 다시 세웁니다. 계속할까요?</p>
            <div className="row">
              <button
                type="button"
                onClick={() => {
                  onConfirmRisk(true);
                }}
                disabled={busy}
              >
                확인하고 재계획
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  onConfirmRisk(false);
                }}
                disabled={busy}
              >
                취소
              </button>
            </div>
          </div>
        )}
      </section>

      <section>
        <h2>시각 변경</h2>
        <label htmlFor="seek-input">이동할 시각</label>
        <input
          id="seek-input"
          type="datetime-local"
          step={1}
          value={seekValue}
          disabled={clockDisabled}
          onChange={(event) => {
            setSeekValue(event.target.value);
            setDirty(true);
          }}
        />
        <button
          type="button"
          onClick={() => {
            onSeek(fromLocalInputValue(seekValue));
            // 이동 요청을 보냈으니 다시 시계를 따라가게 둔다.
            setDirty(false);
          }}
          disabled={clockDisabled}
        >
          시각 변경
        </button>
        <p className="hint">과거로 이동하면 새 run 이 만들어집니다 (§7.2)</p>
      </section>

      <section>
        <h2>재생</h2>
        <div className="row">
          <button type="button" onClick={onPlayPause} disabled={clockDisabled}>
            {clock.is_playing ? "⏸ 정지" : "▶ 재생"}
          </button>
        </div>
        <div className="row">
          {SPEED_OPTIONS.map((multiplier) => (
            <button
              key={multiplier}
              type="button"
              className={clock.speed_multiplier === multiplier ? "active" : ""}
              onClick={() => {
                onSpeed(multiplier);
              }}
              disabled={clockDisabled}
            >
              {multiplier}배속
            </button>
          ))}
        </div>
        <button type="button" className="secondary" onClick={onReset} disabled={busy}>
          처음부터 다시 (reset)
        </button>
      </section>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </aside>
  );
}
